import { Button, Card, Col, Divider, Input, Row, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import React from "react";
import { Controller, useFormContext } from "react-hook-form";
import { DataDiscountCode } from "../../../../redux/@types";
import { InputData, InputNumber, InputSelect, InputText } from "../../../../shared/components";
import { generateCouponCode } from "../../lib/generateCouponCode";

const { Text, Title } = Typography;

interface DiscountCodeFormProps {
  isUpdate: boolean;
  isLoading: boolean;
  onSubmit: React.FormEventHandler<HTMLFormElement>;
}

export const DiscountCodeForm = ({
  isUpdate,
  isLoading,
  onSubmit,
}: DiscountCodeFormProps) => {
  const { control, setValue } = useFormContext<DataDiscountCode>();

  const applyGeneratedCode = React.useCallback(() => {
    setValue("code", generateCouponCode(), {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [setValue]);

  React.useEffect(() => {
    if (!isUpdate) {
      applyGeneratedCode();
    }
  }, [isUpdate, applyGeneratedCode]);

  return (
    <form onSubmit={onSubmit}>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Title level={5} style={{ marginBottom: 4 }}>
            Información general
          </Title>
          <Text type="secondary">
            Define el nombre, código y disponibilidad del cupón.
          </Text>
        </Col>

        <Col span={24}>
          <Text strong>Estado</Text>
          <InputSelect
            name="status"
            control={control}
            dataOptions={[
              { title: "Activo", value: "active" },
              { title: "Inactivo", value: "inactive" },
            ]}
            label="Estado"
          />
        </Col>

        <Col span={24}>
          <Text strong>Nombre del descuento</Text>
          <InputText
            placeHolder="Ej: Código de bienvenida"
            name="name"
            control={control}
          />
        </Col>

        <Col span={12}>
          <Text strong>Código</Text>
          <div style={{ display: "flex", gap: 8, width: "100%" }}>
            <Controller
              name="code"
              control={control}
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  value={field.value ?? ""}
                  placeholder="SUSHI9X2K4"
                  status={fieldState.error ? "error" : ""}
                  style={{ flex: 1, borderRadius: 6, textTransform: "uppercase" }}
                  onChange={(event) => {
                    field.onChange(event.target.value.toUpperCase());
                  }}
                />
              )}
            />
            {!isUpdate && (
              <Button
                type="default"
                icon={<ReloadOutlined />}
                onClick={applyGeneratedCode}
                style={{ flexShrink: 0 }}
              >
                Generar
              </Button>
            )}
          </div>
          {!isUpdate && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Código único generado automáticamente
            </Text>
          )}
        </Col>

        <Col span={12}>
          <Text strong>Cantidad total</Text>
          <InputNumber placeHolder="100" name="quantity" control={control} min />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Usos globales del cupón
          </Text>
        </Col>

        <Col span={24}>
          <Divider style={{ margin: "8px 0" }} />
          <Title level={5} style={{ marginBottom: 4 }}>
            Valor del descuento
          </Title>
          <Text type="secondary">
            Completa solo uno: porcentaje o monto fijo.
          </Text>
        </Col>

        <Col span={24}>
          <Card size="small" style={{ background: "#fff", borderColor: "#f0f0f0" }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Text strong>Porcentaje (%)</Text>
                <InputNumber
                  placeHolder="20"
                  name="value_percentage"
                  control={control}
                  min
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Ej: 20 = 20% off
                </Text>
              </Col>
              <Col span={12}>
                <Text strong>Monto fijo ($)</Text>
                <InputNumber
                  placeHolder="15000"
                  name="value_amount"
                  control={control}
                  min
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Ej: 50000 pesos
                </Text>
              </Col>
            </Row>
          </Card>
        </Col>

        <Col span={24}>
          <Divider style={{ margin: "8px 0" }} />
          <Title level={5} style={{ marginBottom: 4 }}>
            Condiciones de uso
          </Title>
        </Col>

        <Col span={12}>
          <Text strong>Usos por usuario</Text>
          <InputNumber placeHolder="1" name="uses_by_user" control={control} min />
        </Col>

        <Col span={12}>
          <Text strong>Compra mínima ($)</Text>
          <InputNumber
            placeHolder="0"
            name="order_min_amount"
            control={control}
            min
          />
          <Text type="secondary" style={{ fontSize: 12 }}>
            Usa 0 si no aplica
          </Text>
        </Col>

        <Col span={12}>
          <Text strong>Fecha de inicio</Text>
          <InputData placeHolder="01/01/2026" name="date_start" control={control} />
        </Col>

        <Col span={12}>
          <Text strong>Fecha de finalización</Text>
          <InputData placeHolder="31/12/2026" name="date_end" control={control} />
        </Col>

        <Col span={24} style={{ marginTop: 8, marginBottom: 8 }}>
          <Button
            htmlType="submit"
            type="primary"
            shape="round"
            size="large"
            block
            loading={isLoading}
            style={{
              height: 48,
              fontWeight: 600,
              boxShadow: "0 4px 10px rgba(233, 87, 53, 0.3)",
            }}
          >
            {isUpdate ? "Guardar cambios" : "Crear cupón"}
          </Button>
        </Col>
      </Row>
    </form>
  );
};
